// ============================================================
// BarOptionsButton
// ============================================================
// A small persistent button (⋯) shown in the top-right of
// every bar. On click/tap it opens a popover with all
// bar-level controls:
//   - Annotation (add/edit/remove)
//   - Time signature override
//   - 8th notes toggle (slash mode only)
//
// This makes all bar controls accessible on touch devices
// where hover never fires.
// ============================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { Bar, Section, TimeSignature } from '../../core/types';
import { useSongStore, effectiveTimeSignature } from '../../store/songStore';
import { AnnotationField } from './AnnotationField';

const TIME_SIGNATURES: TimeSignature[] = [
  { numerator: 4, denominator: 4 },
  { numerator: 3, denominator: 4 },
  { numerator: 2, denominator: 4 },
  { numerator: 6, denominator: 8 },
  { numerator: 9, denominator: 8 },
  { numerator: 5, denominator: 4 },
  { numerator: 7, denominator: 8 },
];

function tsLabel(ts: TimeSignature) { return `${ts.numerator}/${ts.denominator}`; }
function tsEqual(a: TimeSignature, b: TimeSignature) {
  return a.numerator === b.numerator && a.denominator === b.denominator;
}

interface BarOptionsButtonProps {
  bar: Bar;
  section: Section;
  showEighthNotes?: boolean; // only in slash mode
}

export function BarOptionsButton({ bar, section, showEighthNotes = false }: BarOptionsButtonProps) {
  const song                = useSongStore((s) => s.song);
  const setBarNote          = useSongStore((s) => s.setBarNote);
  const setBarTimeSignature = useSongStore((s) => s.setBarTimeSignature);
  const toggleEighthNotes   = useSongStore((s) => s.toggleEighthNotes);

  const [open, setOpen]   = useState(false);
  const ref               = useRef<HTMLDivElement>(null);
  const effectiveTs       = effectiveTimeSignature(song, bar);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    document.addEventListener('touchstart', handle);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('touchstart', handle);
    };
  }, [open]);

  const hasOverride = bar.timeSignature !== null;
  const hasNote     = !!bar.note;
  const hasDot      = hasOverride || hasNote || bar.eighthNotes;

  return (
    <div
      ref={ref}
      className="bar-options-btn print:hidden"
      style={{
        position: 'absolute',
        top: 3,
        right: 4,
        zIndex: 10,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Trigger button */}
      <button
        onPointerDown={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        style={{
          width: 18,
          height: 18,
          borderRadius: 3,
          border: open
            ? '1px solid var(--color-accent)'
            : '1px solid transparent',
          background: open ? 'var(--color-hover)' : 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          color: hasDot ? 'var(--color-accent)' : 'var(--color-ink-muted)',
          opacity: open ? 1 : 0.5,
          transition: 'opacity 120ms, border-color 120ms',
          padding: 0,
          lineHeight: 1,
        }}
        title="Bar options"
        className="group-hover/bar:opacity-100"
      >
        {hasDot ? '●' : '⋯'}
      </button>

      {/* Popover */}
      {open && (
        <div
          className="dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            padding: '10px 12px',
            minWidth: 180,
            zIndex: 200,
          }}
        >
          {/* Annotation */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontFamily: 'var(--font-label)', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--color-ink-muted)', marginBottom: 4 }}>
              Note
            </div>
            <AnnotationField
              value={bar.note ?? null}
              onChange={(note) => setBarNote(section.id, bar.id, note)}
              placeholder="Add note…"
              showHint={true}
            />
            {bar.note && (
              <button
                onClick={() => setBarNote(section.id, bar.id, null)}
                style={{ fontSize: 9, color: 'var(--color-error)', fontFamily: 'var(--font-mono)', background: 'none', border: 'none', cursor: 'pointer', marginTop: 3, padding: 0 }}
              >
                ✕ Remove
              </button>
            )}
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid var(--color-rule)', marginBottom: 10 }} />

          {/* Time signature */}
          <div style={{ marginBottom: showEighthNotes ? 10 : 0 }}>
            <div style={{ fontSize: 9, fontFamily: 'var(--font-label)', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--color-ink-muted)', marginBottom: 6 }}>
              Time signature
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {TIME_SIGNATURES.map((ts) => {
                const isActive    = hasOverride && tsEqual(ts, bar.timeSignature!);
                const isInherited = !hasOverride && tsEqual(ts, effectiveTs);
                return (
                  <button
                    key={tsLabel(ts)}
                    onClick={() => {
                      if (isActive) {
                        setBarTimeSignature(section.id, bar.id, null);
                      } else {
                        setBarTimeSignature(section.id, bar.id, ts);
                      }
                    }}
                    style={{
                      fontSize: 11, fontFamily: 'var(--font-mono)',
                      padding: '2px 7px', borderRadius: 4,
                      border: isActive
                        ? '1px solid var(--color-accent)'
                        : '1px solid var(--color-rule)',
                      background: isActive
                        ? 'var(--color-accent)'
                        : 'transparent',
                      color: isActive
                        ? 'white'
                        : isInherited
                        ? 'var(--color-ink-primary)'
                        : 'var(--color-ink-muted)',
                      fontWeight: isInherited && !isActive ? 700 : 400,
                      cursor: 'pointer',
                    }}
                  >
                    {tsLabel(ts)}
                  </button>
                );
              })}
            </div>
            {hasOverride && (
              <button
                onClick={() => setBarTimeSignature(section.id, bar.id, null)}
                style={{ fontSize: 9, color: 'var(--color-error)', fontFamily: 'var(--font-mono)', background: 'none', border: 'none', cursor: 'pointer', marginTop: 5, padding: 0 }}
              >
                ✕ Reset to default
              </button>
            )}
          </div>

          {/* 8th notes (slash mode only) */}
          {showEighthNotes && (
            <>
              <div style={{ borderTop: '1px solid var(--color-rule)', marginBottom: 10 }} />
              <div>
                <div style={{ fontSize: 9, fontFamily: 'var(--font-label)', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--color-ink-muted)', marginBottom: 6 }}>
                  Subdivisions
                </div>
                <button
                  onClick={() => toggleEighthNotes(section.id, bar.id)}
                  style={{
                    fontSize: 11, fontFamily: 'var(--font-mono)',
                    padding: '3px 10px', borderRadius: 4,
                    border: bar.eighthNotes
                      ? '1px solid var(--color-accent)'
                      : '1px solid var(--color-rule)',
                    background: bar.eighthNotes ? 'var(--color-accent)' : 'transparent',
                    color: bar.eighthNotes ? 'white' : 'var(--color-ink-muted)',
                    cursor: 'pointer',
                  }}
                >
                  {bar.eighthNotes ? '♪×8 — on' : '♪×4 — off'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
